import React, { useCallback, useRef } from "react";
import {
  Stack,
  Alert,
  CircularProgress,
  Typography,
  Tooltip,
} from "@mui/material";
import { CheckCircle, ArrowBack } from "@mui/icons-material";
import { PrimaryButton, SecondaryButton } from "@/components/common";
import { useQuestionnaireForm } from "./hooks/useQuestionnaireForm";
import { FormItem } from "./FormBuilder/FormItem";
import { LayoutOptions, ItemLayoutConfig } from "./FormBuilder/types";

interface QuestionnaireFormProps {
  questionnaire: any;
  onBack: () => void;
  onSubmit: () => void;
  loading?: boolean;
  patientId?: string;
  encounterId?: string;
  onRefresh?: () => void;
  layoutOptions?: LayoutOptions;
  itemLayoutConfig?: ItemLayoutConfig;
}

export function QuestionnaireForm({
  questionnaire,
  onBack,
  onSubmit,
  loading = false,
  patientId,
  encounterId,
  layoutOptions,
  itemLayoutConfig,
  onRefresh,
}: QuestionnaireFormProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const {
    answers,
    errors,
    touched,
    setErrors,
    handleAnswerChange,
    validateField,
    shouldHideItem,
    handleSubmit,
    submitLoading,
    submitError,
    clearError,
  } = useQuestionnaireForm({ questionnaire, patientId, encounterId });

  const scrollToFirstError = useCallback((linkId: string) => {
    const fieldEl = document.querySelector(`[data-linkid="${linkId}"]`);
    if (fieldEl) {
      fieldEl.scrollIntoView({ behavior: "smooth", block: "center" });
      const focusable = fieldEl.querySelector<HTMLElement>(
        'input:not([type="hidden"]), select, textarea'
      );
      if (focusable) {
        focusable.focus();
      }
    }
  }, []);

  const renderQuestionItem = (item: any, level: number = 0) => {
    const isTouched = touched[item.linkId];
    const isHidden = shouldHideItem(item);

    React.useEffect(() => {
      if (isHidden) {
        setErrors((prev) => ({ ...prev, [item.linkId]: "" }));
        return;
      }
      if (isTouched) {
        const fieldError = validateField(item, answers[item.linkId]);
        setErrors((prev) => ({
          ...prev,
          [item.linkId]: fieldError ?? "",
        }));
      }
    }, [answers[item.linkId], isTouched, isHidden, item, validateField, setErrors]);

    return (
      <FormItem
        item={item}
        level={level}
        answers={answers}
        errors={errors}
        touched={touched}
        onAnswerChange={handleAnswerChange}
        renderChild={renderQuestionItem}
        layoutOptions={layoutOptions}
        itemLayoutConfig={itemLayoutConfig}
        shouldHideItem={shouldHideItem}
      />
    );
  };

  const onFormSubmit = () => {
    handleSubmit(onSubmit, onRefresh, scrollToFirstError);
  };

  return (
    <Stack width="100%" maxHeight="54vh" pt={2}>
      <Stack ref={scrollContainerRef} sx={{ maxHeight: "70vh", overflowY: "auto" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          mb={1}
        >
          <Tooltip
            title={questionnaire.title || questionnaire.name || "Questionário"}
            placement="top"
            arrow
          >
            <Typography
              variant="subtitle2"
              fontWeight={500}
              color="primary.main"
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {questionnaire.title || questionnaire.name || "Questionário"}
            </Typography>
          </Tooltip>
          <SecondaryButton onClick={onBack} height="32px" width="fit-content">
            <ArrowBack fontSize="small" sx={{ mr: 1 }} /> Voltar
          </SecondaryButton>
        </Stack>

        {questionnaire.item?.map((item: any) => renderQuestionItem(item))}
        <Stack direction="row" justifyContent="space-between" mt={3}>
          <PrimaryButton
            onClick={onFormSubmit}
            height="32px"
            width="fit-content"
            startIcon={<CheckCircle />}
            disabled={
              loading ||
              submitLoading ||
              Object.keys(errors).some((key) => errors[key])
            }
          >
            {loading || submitLoading ? (
              <CircularProgress size={20} />
            ) : (
              "Salvar Respostas"
            )}
          </PrimaryButton>
        </Stack>
      </Stack>
      {Object.keys(errors).some((key) => errors[key]) && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Existem campos com erros. Por favor, corrija-os antes de continuar.
        </Alert>
      )}
      {submitError && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={clearError}>
          {submitError}
        </Alert>
      )}
    </Stack>
  );
}
